import {
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  ViewChild,
  ViewContainerRef,
  EventEmitter,
  SimpleChanges,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FlexLayoutModule } from '@angular/flex-layout';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ReportsService } from '@app/core/services/reports.service';
import {
  InputControl,
  InputControls,
  Report,
  ReportDetailsData,
  ReportUnit,
} from '@app/shared/types';
import { EMPTY, Subscription, debounceTime, from, switchMap, catchError } from 'rxjs';
import { CONTROLS_MAP } from '../controls/controls.utils';

export interface ComponentOutput {
  id: string;
  value: string | string[];
  isValid?: boolean;
  error?: string;
}

export interface ReportPreviewUpdate {
  html?: string | null;
  config: Report;
  loading?: boolean;
  error?: string | null;
  hasUnappliedChanges?: boolean;
}

export function hasValueProperty(state: any): state is { value: string | string[] } {
  return state && 'value' in state;
}

@Component({
  selector: 'app-report-details',
  standalone: true,
  imports: [
    CommonModule,
    FlexLayoutModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './report-details.component.html',
  styleUrls: ['./report-details.component.css'],
})
export class ReportDetailsComponent implements OnInit, OnDestroy {
  reportForm = new FormGroup({});
  @Input() inputData?: ReportDetailsData;

  @ViewChild('container', { read: ViewContainerRef })
  container!: ViewContainerRef;

  @Output() previewUpdated = new EventEmitter<ReportPreviewUpdate>();

  public report!: ReportUnit;
  public selectedData: ComponentOutput[] = [];
  public isLoading = false;
  public isGeneratingReport = false;
  public hasUnappliedChanges = false;
  public hasGeneratedOnce = false;

  private getInputControlValuesSub!: Subscription;
  private componentOutputSubs: Subscription[] = [];
  private inputControlRefs: Map<string, any> = new Map();
  private mandatoryControlIds: Set<string> = new Set();
  private lastSentValues: Record<string, string | string[]> = {};
  private currentReportUri: string | null = null;

  constructor(
    private dialog: MatDialog,
    private readonly reportsService: ReportsService,
    private cd: ChangeDetectorRef,
    @Optional() public dialogRef: MatDialogRef<ReportDetailsComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: ReportDetailsData
  ) {}

  ngOnInit(): void {
    const finalData = this.inputData || this.data;
    if (finalData?.report) {
      this.report = finalData.report;
      if (this.currentReportUri !== this.report.uri) {
        this.loadReportControls();
        this.currentReportUri = this.report.uri;
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['inputData'] && this.inputData?.report) {
      const newUri = this.inputData.report.uri;
      if (this.currentReportUri === newUri) {
        return;
      }
      this.report = this.inputData.report;
      this.currentReportUri = newUri;
      this.hasGeneratedOnce = false;
      this.hasUnappliedChanges = false;
      this.loadReportControls();
    }
  }

  ngOnDestroy(): void {
    this.getInputControlValuesSub?.unsubscribe();
    this.componentOutputSubs.forEach((sub) => sub.unsubscribe());
    this.inputControlRefs.forEach((ref) => ref.destroy());
    this.inputControlRefs.clear();
  }

  private loadReportControls(): void {
    if (!this.report || !this.report.uri) return;

    this.isLoading = true;
    this.getInputControlValuesSub?.unsubscribe();

    this.getInputControlValuesSub = this.reportsService
      .getInputControlValues(this.report.uri)
      .subscribe({
        next: (res: InputControls) => {
          this.isLoading = false;

          if (res && res.inputControl && res.inputControl.length > 0) {
            this.initializeControls(res);
          } else {
            this.container.clear();
          }

          this.cd.detectChanges();
        },
        error: (err) => {
          this.isLoading = false;
          console.error('Error API filters:', err);
        },
      });
  }

  private initializeControls(inputControls: InputControls): void {
    this.container.clear();
    this.inputControlRefs.clear();
    this.mandatoryControlIds.clear();
    this.componentOutputSubs.forEach((sub) => sub.unsubscribe());
    this.componentOutputSubs = [];
    this.reportForm = new FormGroup({});

    inputControls.inputControl.forEach((control: InputControl) => {
      const componentType = CONTROLS_MAP[control.type as keyof typeof CONTROLS_MAP];
      if (!componentType) return;

      if (control.mandatory) {
        this.mandatoryControlIds.add(control.id);
      }

      const componentRef = this.container.createComponent(componentType);
      componentRef.setInput('data', {
        state: control.state,
        label: control.label,
        mandatory: control.mandatory,
        dataType: control.dataType,
        visible: control.visible,
      });

      this.inputControlRefs.set(control.id, componentRef);

      const initialValue = hasValueProperty(control.state) ? control.state.value : null;
      
      const validators = control.mandatory ? [Validators.required] : [];
      this.reportForm.addControl(control.id, new FormControl(initialValue, validators));

      if (initialValue !== null && initialValue !== undefined) {
        this.updateSelectedData({ id: control.id, value: initialValue });
      }

      const sub = componentRef.instance.output?.pipe(
        debounceTime(300),
        switchMap((res: ComponentOutput) => {
          const currValue = JSON.stringify(res.value);
          if (this.lastSentValues[res.id] === currValue) return EMPTY;
          this.lastSentValues[res.id] = currValue;

          const formControl = this.reportForm.get(res.id);
          if (formControl) {
            formControl.setValue(res.value, { emitEvent: false });
            if (res.isValid === false) {
              formControl.setErrors({ invalid: true });
            } else if (!control.mandatory || (res.value && res.value.length > 0)) {
              formControl.setErrors(null);
            }
          }

          this.updateSelectedData(res);

          if (!this.isValidDateRange()) {
          return EMPTY; 
        }

           if (this.hasGeneratedOnce) {
              this.hasUnappliedChanges = true;
            }

          const body: Record<string, string[]> = {};
          this.selectedData.forEach((el) => {
            body[el.id] = Array.isArray(el.value) ? el.value : [el.value];
          });
          return this.reportsService.refreshInputControlValues(this.report.uri, body);
        })
      ).subscribe((res: InputControls) => {
        this.updateControls(res);
      });

      if (sub) this.componentOutputSubs.push(sub);
    });
    this.cd.detectChanges();
  }

  public isFormValidForGeneration(): boolean {
    if (this.reportForm.invalid) return false;

    for (const controlId of this.mandatoryControlIds) {
      const val = this.reportForm.get(controlId)?.value;
      if (!val || (Array.isArray(val) && val.length === 0)) {
        return false;
      }
    }
    return true;
  }

  public onGenerateReport(): void {
    if (!this.isFormValidForGeneration() || this.isGeneratingReport) return;

    this.isGeneratingReport = true;
    this.hasUnappliedChanges = false;
    const previewConfig = this.buildReportConfig();

    this.previewUpdated.emit({ config: previewConfig, loading: true, hasUnappliedChanges: false });

    this.reportsService
      .runReport(previewConfig)
      .pipe(
        switchMap((res) => this.reportsService.getReportExecutionStatus(res.requestId)),
        switchMap((res) => this.reportsService.getReportOutput(res.requestId, res.exports[0].id)),
        switchMap((file: Blob) => from(file.text())),
        catchError((err) => {
          console.error('Report preview error:', err);
          this.isGeneratingReport = false;
          this.previewUpdated.emit({
            config: previewConfig,
            loading: false,
            error: 'Došlo je do greške pri generisanju pregleda. Pokušajte ponovo.',
            hasUnappliedChanges: false,
          });
          return EMPTY;
        })
      )
      .subscribe((htmlText: string) => {
        this.isGeneratingReport = false;
        this.hasGeneratedOnce = true;
        const trimmedHtml = htmlText.trim();
        this.previewUpdated.emit({
          html: trimmedHtml,
          config: previewConfig,
          loading: false,
          hasUnappliedChanges: false,
        });
        this.cd.detectChanges();
      });
  }

  private buildReportConfig(): Report {
    return {
      reportUnitUri: this.report.uri,
      async: true,
      freshData: false,
      saveDataSnapshot: false,
      outputFormat: 'html',
      interactive: true,
      ignorePagination: false,
      parameters: {
        reportParameter: this.selectedData.map((data) => ({
          name: data.id,
          value: Array.isArray(data.value) ? data.value : [data.value],
        })),
      },
    };
  }

  private updateSelectedData(res: ComponentOutput): void {
    const index = this.selectedData.findIndex((el) => el.id === res.id);
    if (index === -1) {
      this.selectedData.push(res);
    } else {
      this.selectedData.splice(index, 1, res);
    }
  }

  private updateControls(inputControls: InputControls): void {
    inputControls.inputControl.forEach((control: InputControl) => {
      const ref = this.inputControlRefs.get(control.id);
      if (ref) {
        ref.setInput('data', {
          state: control.state,
          label: control.label,
          mandatory: control.mandatory,
          dataType: control.dataType,
          visible: control.visible,
        });
      }
    });
  }

  private parseDate(val: string | string[] | Date): Date {
    const rawValue = Array.isArray(val) ? val[0] : val;
    return new Date(rawValue);
  }

  private isValidDateRange(): boolean {
    const fromData = this.selectedData.find(d => d.id === 'P_DATE_FROM' || d.id === 'P_FromDate');
    const toData = this.selectedData.find(d => d.id === 'P_DATE_TO' || d.id === 'P_ToDate');
    const toControl = this.reportForm.get(toData?.id ?? '');

    if (!fromData?.value || !toData?.value) {
      if (toControl?.hasError('dateRangeInvalid')) {
        const currentErrors = { ...toControl.errors };
        delete currentErrors['dateRangeInvalid'];
        toControl.setErrors(Object.keys(currentErrors).length > 0 ? currentErrors : null);
      }
      return true;
    }

    const fromDate = this.parseDate(fromData.value);
    const toDate = this.parseDate(toData.value);

    if (toDate < fromDate) {
      toControl?.setErrors({ dateRangeInvalid: true });
      return false;
    }

    if (toControl?.hasError('dateRangeInvalid')) {
      const currentErrors = { ...toControl.errors };
      delete currentErrors['dateRangeInvalid'];
      toControl.setErrors(Object.keys(currentErrors).length > 0 ? currentErrors : null);
    }

    return true;
  }
}